import { useState } from 'react';

interface Emotion {
  name: string;
  cardTitle: string;
  image: string;
  description: string;
  artist: string;
  artwork: string;
  year: string;
}

export default function HeroSection() {
  const [selectedEmotion, setSelectedEmotion] = useState<Emotion | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  const emotions: Emotion[] = [
    {
      name: 'Surprised',
      cardTitle: 'Surprise',
      image: 'https://i.ibb.co/bGq7f3L/Surprise-White.png',
      description: 'Surprise is a rapid orienting emotion that appears when something unexpected happens. Somatically it may feel like a quick inhale, widened eyes, or a sudden jolt of alertness in the body. It signals needs for updated information, clarity, and reorientation to a changed situation.',
      artist: 'Gustave Courbet',
      artwork: 'The Desperate Man',
      year: '1843–1845'
    },
    {
      name: 'Peaceful',
      cardTitle: 'Peace',
      image: 'https://i.ibb.co/3mRGKWkQ/Orange-Peaceful.jpg',
      description: 'Peace is a grounded state of regulation and inner balance. In the body it shows up as steady breath, relaxed muscles, and a sense of spaciousness. It signals that needs for safety, clarity, and stability are met.',
      artist: 'Frederic Leighton',
      artwork: 'Flaming June',
      year: '1895'
    },
    {
      name: 'Angry',
      cardTitle: 'Anger',
      image: 'https://i.ibb.co/HL2y8f0N/Anger-red.jpg',
      description: 'Anger arises when a boundary is crossed or when something feels unjust. Somatically it may feel like heat, tightness, or mobilizing energy in the arms, jaw, or chest. It signals needs for protection, respect, fairness, or assertive action.',
      artist: 'Nicholas Roerich',
      artwork: 'The Fiery Face',
      year: '1924'
    },
    {
      name: 'Disgusted',
      cardTitle: 'Disgust',
      image: 'https://i.ibb.co/pBF2LNTQ/Disgust-Brown.jpg',
      description: 'Disgust appears when something feels toxic, harmful, or violating your values. In the body it can show up as nausea, recoil, or an urge to push something away. It signals needs for safety, integrity, and healthy boundaries.',
      artist: 'Francisco Goya',
      artwork: 'Saturn Devouring His Son',
      year: '1819–1823'
    },
    {
      name: 'Happy',
      cardTitle: 'Happiness',
      image: 'https://i.ibb.co/zT7MkWkN/Happy-Yellow-min.jpg',
      description: 'Happiness is a sustained sense of wellbeing that comes from living in alignment with your values and experiencing meaningful moments. In the body it may feel like warmth, ease, and a relaxed, open posture. It often signals that needs for connection, fulfillment, balance, or purpose are being met.',
      artist: 'Gustav Klimt',
      artwork: 'The Kiss',
      year: '1907–1908'
    },
    {
      name: 'Sad',
      cardTitle: 'Sadness',
      image: 'https://i.ibb.co/yFS5Zt2S/Sad-Green.jpg',
      description: 'Sadness appears when you lose something meaningful or when a need goes unmet for a long time. Somatic experience may include heaviness in the chest, slower breath, and inward, collapsing sensations. It often points to needs for comfort, support, rest, or letting go.',
      artist: 'John Everett Millais',
      artwork: 'Ophelia',
      year: '1851–1852'
    }
  ];

  const scrollToBooking = () => {
    const element = document.getElementById('services-pricing');
    if (element) {
      const headerHeight = 80;
      const elementPosition = element.offsetTop - headerHeight;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
  };

  const scrollToDrawingInsight = () => {
    const element = document.getElementById('drawing-insight');
    if (element) {
      const headerHeight = 80;
      const elementPosition = element.offsetTop - headerHeight;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
  };

  const openModal = (emotion: Emotion) => {
    setSelectedEmotion(emotion);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedEmotion(null);
    document.body.style.overflow = 'auto';
  };

  return (
    <>
      <section 
        id="home" 
        className="relative w-full min-h-screen py-12 md:py-4"
        style={{
          backgroundImage: 'url(https://static.readdy.ai/image/36990eb3aa57e3c7d396ac7e72f77043/c586e8cd570c845562040c6a1f4c820b.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'scroll'
        }}
      >
        {/* Dark overlay for better text readability */}
        <div className="absolute inset-0 bg-[#1c1208]/40"></div>

        {/* Content wrapper with relative positioning */}
        <div className="relative z-10">
          {/* Desktop: Full-width emotion wheel */}
          <div className="hidden md:block w-full min-h-screen flex flex-col">
            {/* Headline and Subtitle - Compact spacing */}
            <div className="text-center pt-4 pb-2">
              <h1 
                className="text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight mb-2 cinematic-text-light"
              >
                Healing through creative expression
              </h1>
              <p className="text-gray-300 text-lg lg:text-xl max-w-3xl mx-auto px-6 leading-snug">
                Art therapist & addiction counselor with 10+ years of clinical experience. Online sessions worldwide · In-person in Chiang Mai.
              </p>
            </div>

            {/* Caption above wheel */}
            <p className="text-center text-gray-400 text-sm lg:text-base pt-1 lg:pt-2">
              Start by exploring how you feel right now ↓
            </p>

            {/* Full-width Emotion Wheel Container - Minimal vertical spacing */}
            <div className="flex-1 flex items-center justify-center px-4 py-2">
              <div className="relative w-full max-w-none" style={{ height: 'min(56vh, 56vw)', width: 'min(56vh, 56vw)' }}>
                <svg 
                  viewBox="0 0 400 400" 
                  className="w-full h-full"
                  preserveAspectRatio="xMidYMid meet"
                >
                  <defs>
                    {emotions.map((emotion, index) => {
                      const angle = (360 / emotions.length) * index;
                      const nextAngle = (360 / emotions.length) * (index + 1);
                      const startRad = (angle - 90) * (Math.PI / 180);
                      const endRad = (nextAngle - 90) * (Math.PI / 180);
                      
                      const x1 = 200 + 180 * Math.cos(startRad);
                      const y1 = 200 + 180 * Math.sin(startRad);
                      const x2 = 200 + 180 * Math.cos(endRad);
                      const y2 = 200 + 180 * Math.sin(endRad);

                      const largeArcFlag = 0;

                      const pathData = `M 200 200 L ${x1} ${y1} A 180 180 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;

                      return (
                        <clipPath key={`clip-${index}`} id={`slice-${index}`}>
                          <path d={pathData} />
                        </clipPath>
                      );
                    })}
                  </defs>

                  {emotions.map((emotion, index) => {
                    const angle = (360 / emotions.length) * index;
                    const nextAngle = (360 / emotions.length) * (index + 1);
                    const midAngle = (angle + nextAngle) / 2;
                    const midRad = (midAngle - 90) * (Math.PI / 180);
                    
                    const textX = 200 + 120 * Math.cos(midRad);
                    const textY = 200 + 120 * Math.sin(midRad);

                    return (
                      <g key={index}>
                        <image
                          href={emotion.image}
                          x="20"
                          y="20"
                          width="360"
                          height="360"
                          clipPath={`url(#slice-${index})`}
                          preserveAspectRatio="xMidYMid slice"
                          className="transition-all duration-300 cursor-pointer hover:opacity-90"
                          style={{ transformOrigin: '200px 200px' }}
                          onClick={() => openModal(emotion)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.04)';
                            e.currentTarget.style.filter = 'drop-shadow(0 8px 16px rgba(0,0,0,0.3))';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.filter = 'none';
                          }}
                          role="button"
                          aria-label={`${emotion.name} emotion`}
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openModal(emotion);
                            }
                          }}
                        />
                        
                        <text
                          x={textX}
                          y={textY}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          className="pointer-events-none select-none"
                          style={{ 
                            fontSize: '18px',
                            fontWeight: '700',
                            fill: '#FFFFFF',
                            textShadow: '2px 2px 8px rgba(0,0,0,0.8)',
                            filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.9))'
                          }}
                        >
                          {emotion.name}
                        </text>
                      </g>
                    );
                  })}

                  {/* Center Text: "I feel…" */}
                  <text
                    x="200"
                    y="200"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="pointer-events-none select-none"
                    style={{ 
                      fontSize: '28px',
                      fontWeight: '700',
                      fill: '#FFFFFF',
                      textShadow: '2px 2px 8px rgba(0,0,0,0.8)',
                      filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.9))'
                    }}
                  >
                    I FEEL...
                  </text>
                </svg>
              </div>
            </div>

            {/* CTA Buttons - Minimal spacing from wheel */}
            <div className="flex items-center justify-center gap-4 pt-2 pb-4">
              <button
                onClick={scrollToBooking}
                className="cinematic-accent-bg hover:cinematic-accent-hover text-white px-10 py-4 rounded-xl text-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer inline-flex items-center shadow-lg w-48 justify-center"
              >
                Book a Session
                <i className="ri-arrow-right-line ml-2"></i>
              </button>
              <button
                onClick={scrollToDrawingInsight}
                className="text-white hover:bg-amber-500 hover:text-white px-10 py-4 rounded-xl text-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer inline-flex items-center shadow-lg bg-white/10 backdrop-blur-sm w-48 justify-center"
              >
                Try for Free
                <i className="ri-arrow-right-line ml-2"></i>
              </button>
            </div>
          </div>

          {/* Mobile: Content-based emotion wheel (NO VERTICAL CENTERING) */}
          <div className="block md:hidden w-full px-4">
            {/* Headline and Subtitle - Compact spacing */}
            <div className="text-center pt-6 pb-0">
              <h1 
                className="text-2xl font-bold mb-2 cinematic-text-light"
              >
                Healing through creative expression
              </h1>
              <p className="text-gray-300 text-sm px-2">
                Art therapist & addiction counselor with 10+ years of clinical experience. Online sessions worldwide · In-person in Chiang Mai.
              </p>
            </div>

            {/* Caption above wheel */}
            <p className="text-center text-gray-400 text-xs pt-1">
              Start by exploring how you feel right now ↓
            </p>

            {/* Mobile Emotion Wheel Container - MINIMAL TOP SPACING (8-12px only) */}
            <div className="-mt-1 mb-4 flex justify-center">
              <div className="relative w-[96vw] h-[96vw] max-w-[96vh] max-h-[96vh]">
                <svg 
                  viewBox="0 0 400 400" 
                  className="w-full h-full"
                  preserveAspectRatio="xMidYMid meet"
                >
                  <defs>
                    {emotions.map((emotion, index) => {
                      const angle = (360 / emotions.length) * index;
                      const nextAngle = (360 / emotions.length) * (index + 1);
                      const startRad = (angle - 90) * (Math.PI / 180);
                      const endRad = (nextAngle - 90) * (Math.PI / 180);
                      
                      const x1 = 200 + 180 * Math.cos(startRad);
                      const y1 = 200 + 180 * Math.sin(startRad);
                      const x2 = 200 + 180 * Math.cos(endRad);
                      const y2 = 200 + 180 * Math.sin(endRad);

                      const largeArcFlag = 0;

                      const pathData = `M 200 200 L ${x1} ${y1} A 180 180 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;

                      return (
                        <clipPath key={`clip-mobile-${index}`} id={`slice-mobile-${index}`}>
                          <path d={pathData} />
                        </clipPath>
                      );
                    })}
                  </defs>

                  {emotions.map((emotion, index) => {
                    const angle = (360 / emotions.length) * index;
                    const nextAngle = (360 / emotions.length) * (index + 1);
                    const midAngle = (angle + nextAngle) / 2;
                    const midRad = (midAngle - 90) * (Math.PI / 180);
                    
                    const textX = 200 + 110 * Math.cos(midRad);
                    const textY = 200 + 110 * Math.sin(midRad);

                    return (
                      <g key={index}>
                        <image
                          href={emotion.image}
                          x="20"
                          y="20"
                          width="360"
                          height="360"
                          clipPath={`url(#slice-mobile-${index})`}
                          preserveAspectRatio="xMidYMid slice"
                          className="cursor-pointer active:opacity-90"
                          onClick={() => openModal(emotion)}
                          role="button"
                          aria-label={`${emotion.name} emotion`}
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openModal(emotion);
                            }
                          }}
                        />
                        
                        <text
                          x={textX}
                          y={textY}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          className="pointer-events-none select-none"
                          style={{ 
                            fontSize: '22px',
                            fontWeight: '700',
                            fill: '#FFFFFF',
                            textShadow: '2px 2px 8px rgba(0,0,0,0.8)',
                            filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.9))'
                          }}
                        >
                          {emotion.name}
                        </text>
                      </g>
                    );
                  })}

                  {/* Center Text: "I feel…" */}
                  <text
                    x="200"
                    y="200"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="pointer-events-none select-none"
                    style={{ 
                      fontSize: '22px',
                      fontWeight: '700',
                      fill: '#FFFFFF',
                      textShadow: '2px 2px 8px rgba(0,0,0,0.8)',
                      filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.9))'
                    }}
                  >
                    I FEEL...
                  </text>
                </svg>
              </div>
            </div>

            {/* CTA Buttons - Normal spacing under wheel */}
            <div className="flex items-center justify-center gap-3 pb-8">
              <button
                onClick={scrollToBooking}
                className="cinematic-accent-bg hover:cinematic-accent-hover text-white px-8 py-3 rounded-xl text-base font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer inline-flex items-center shadow-lg w-44 justify-center"
              >
                Book a Session
                <i className="ri-arrow-right-line ml-2"></i>
              </button>
              <button
                onClick={scrollToDrawingInsight}
                className="text-white hover:bg-amber-500 hover:text-white px-8 py-3 rounded-xl text-base font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer inline-flex items-center shadow-lg bg-white/10 backdrop-blur-sm w-44 justify-center"
              >
                Try for Free
                <i className="ri-arrow-right-line ml-2"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Full-Screen Modal - No Scroll, Auto-Scaled Typography */}
      {selectedEmotion && (
        <div 
          className="fixed inset-0 bg-[#1c1208]/95 z-[9999] flex items-center justify-center p-4"
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
        >
          <div 
            className="relative w-full h-full max-w-5xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeModal}
              className="absolute top-2 right-2 md:top-4 md:right-4 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-[#1c1208] rounded-full text-white hover:bg-[#2a1a0e] transition-all cursor-pointer z-10"
              aria-label="Close modal"
            >
              <i className="ri-close-line text-xl md:text-2xl"></i>
            </button>

            <div className="bg-white rounded-2xl overflow-hidden flex flex-col h-full">
              {/* Image Section - Fixed Height */}
              <div className="relative w-full flex-shrink-0" style={{ height: 'clamp(200px, 40vh, 400px)' }}>
                <img
                  src={selectedEmotion.image}
                  alt={selectedEmotion.name}
                  className="w-full h-full object-contain bg-gray-100"
                />
              </div>

              {/* Artwork Caption */}
              <div className="px-4 py-3 md:px-8 md:py-4 border-b border-gray-100">
                <p 
                  className="text-gray-600 text-center"
                  style={{
                    fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)',
                    lineHeight: '1.5'
                  }}
                >
                  {selectedEmotion.artist} — <em>{selectedEmotion.artwork}</em>, {selectedEmotion.year}
                </p>
              </div>

              {/* Text Section - Flexible with Auto-Scaled Typography */}
              <div className="flex-1 flex flex-col justify-center px-4 py-4 md:px-8 md:py-6 overflow-hidden">
                <h2 
                  id="modal-title"
                  className="font-bold mb-2 md:mb-4 cinematic-text-dark"
                  style={{ 
                    fontSize: 'clamp(1.5rem, 4vw, 3rem)',
                    lineHeight: '1.2'
                  }}
                >
                  {selectedEmotion.cardTitle}
                </h2>
                <p 
                  className="text-gray-700"
                  style={{
                    fontSize: 'clamp(0.875rem, 2vw, 1.125rem)',
                    lineHeight: 'clamp(1.4, 1.5, 1.6)'
                  }}
                >
                  {selectedEmotion.description}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
